/**
 * SearchPage.js - Main search page component
 *
 * This component serves as the main search page, integrating SearchBar and SearchResults
 * components with proper layout and SEO optimization.
 */
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { cacheService } from '../../services/CacheService';
import { useSearch } from '../../hooks/useSearch';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';

/**
 * SearchPage component
 * @returns {JSX.Element} SearchPage component
 */
const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';
  const searchState = useSearch({
    autoFocus: true,
    enableRealTimeResults: true,
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-primary">
      <Helmet>
        <title>{query ? `Search: ${query}` : 'Search'} | Sports.TV</title>
        <meta
          name="description"
          content={
            query
              ? `Search results for "${query}" on Sports.TV`
              : 'Search for movies, shows and more on Sports.TV'
          }
        />
        <meta
          property="og:title"
          content={query ? `Search: ${query} | Sports.TV` : 'Search | Sports.TV'}
        />
        <meta
          property="og:description"
          content={
            query
              ? `Search results for "${query}" on Sports.TV`
              : 'Search for movies, shows and more on Sports.TV'
          }
        />
        <link
          rel="canonical"
          href={`${cacheService.getApiUrl()}search${
            query ? `?q=${encodeURIComponent(query)}` : ''
          }`}
        />
      </Helmet>

      <div className="container px-4 py-8 mx-auto">
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={() => navigate('/')}
            className="text-white flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            Home
          </button>
        </div>
        <div className="max-w-2xl mx-auto mb-8">
          <h1 className="mb-6 text-3xl font-bold text-center text-white">Search</h1>
          <SearchBar
            className="w-full"
            placeholder="Search for movies, shows, and more..."
            autoFocus={true}
            searchState={searchState}
          />
        </div>
        <SearchResults searchState={searchState} />
      </div>
    </div>
  );
};

export default SearchPage;
